import { call, put, takeEvery, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { isEqual } from 'lodash';
import { ApisauceInstance } from 'apisauce';

import { ParseHtmlMessage } from '../../components/common';
import { ON_SPONSOR_GIFT } from './payment.redux';
import Api, { CustomError } from '../../services/api-caller';
import FeatureFlagService, {
  CHUR_2882_AC1_IMPLEMENT_APPROVED_PAYMENT,
} from '../../services/FeatureFlag';
import {
  formattedPaymentError,
  GARevenueTracking,
  toastError,
} from '../../utils';
import { ROUTES, TEXT_CONSTANTS } from '../../constants';
import { ON_CLEAR_BASKET, ON_GET_CHILDREN } from '../children';
import {
  dismissPaymentFailureGuidance,
  isPersistentGuidanceEnabled,
  showPaymentFailureGuidance,
} from './payment-failure-guidance';
const getAttributionData = (state) => state.attribution;

// CHUR-2882: every failed submit shows the donor a message. Specific API reasons
// (e.g. Springboard's "The credit card you have entered has expired.") show as
// before; generic, empty, and no-response failures get the recovery guidance.
export function* onSponsorGiftFailure(
  err: any,
  sponsorRequestSucceeded: boolean
) {
  if (sponsorRequestSucceeded) {
    // The donation went through and a later step threw. Don't tell the donor
    // their payment failed; send them on to the thank-you page.
    console.error('Error after sponsor request succeeded', err);
    yield put(push(ROUTES.EXIT.path));
    return;
  }

  if (isEqual(err?.data?.error_key, TEXT_CONSTANTS.PAYMENT_ERROR_KEY)) {
    // Unchanged: payment went through but sponsorship failed.
    toastError(TEXT_CONSTANTS.PAYMENT_ERROR_MSG);
    yield put(push(ROUTES.EXIT.path));
    yield put(ON_SPONSOR_GIFT.failure(false));
    return;
  }

  const hasSpecificApiMessage =
    err instanceof CustomError &&
    typeof err.message === 'string' &&
    err.message.trim() !== '' &&
    err.message !== TEXT_CONSTANTS.DEFAULT_API_ERROR;

  if (hasSpecificApiMessage) {
    toastError(formattedPaymentError(err.message));
  } else {
    showPaymentFailureGuidance();
  }
  yield put(ON_SPONSOR_GIFT.failure(true));
}

// @ts-ignore
export function* onSponsorGiftRequest(api, { payload }) {
  // CHUR-2883: a resubmit closes the guidance from the last failure, so a
  // second failure shows one fresh copy and a success doesn't carry it to the
  // thank-you page.
  if (isPersistentGuidanceEnabled()) {
    dismissPaymentFailureGuidance();
  }
  let submissionId;
  let gaReportedDonationAmount;
  let googleAnalyticsPaymentItems;
  let attributionData: ReturnType<typeof getAttributionData>;
  let sponsorRequestSucceeded = false;
  try {
    const { payload: api_payload, googleAnanlyticsPurchaseEventPayload } =
      payload;
    attributionData = yield select(getAttributionData);
    gaReportedDonationAmount =
      googleAnanlyticsPurchaseEventPayload.gaReportedDonationAmount;
    googleAnalyticsPaymentItems =
      googleAnanlyticsPurchaseEventPayload.googleAnalyticsPaymentItems;

    const apiPayload = { ...api_payload, attribution: attributionData };

    const { res = {} } = yield call(Api.callServer, api, apiPayload);
    sponsorRequestSucceeded = true;
    const { message = '', submission_id } = res || {};
    submissionId = submission_id;
    yield put(ON_SPONSOR_GIFT.success({ mail: api_payload.mail, message }));
    yield put(push(ROUTES.EXIT.path));
    yield put(ON_CLEAR_BASKET());
    yield put(ON_GET_CHILDREN());
  } catch (err: any) {
    if (
      FeatureFlagService.getFlag(
        CHUR_2882_AC1_IMPLEMENT_APPROVED_PAYMENT,
        false
      ) === true
    ) {
      yield call(onSponsorGiftFailure, err, sponsorRequestSucceeded);
      return; // `finally` still runs.
    }
    let message = err.message;
    const data = err.data;
    let isPaymentError = false;
    if (
      data.error_key &&
      isEqual(data.error_key, TEXT_CONSTANTS.PAYMENT_ERROR_KEY)
    ) {
      toastError(TEXT_CONSTANTS.PAYMENT_ERROR_MSG); // If the payment is successful, but an exception occurs after,
      yield put(push(ROUTES.EXIT.path)); // we still want to navigate to the Exit screen.
    } else {
      isPaymentError = true;
      // @ts-ignore
      if (message == TEXT_CONSTANTS.DEFAULT_API_ERROR) {
        message = TEXT_CONSTANTS.PAYMENT_PROCESSS_ERROR_MSG;
      }
      toastError(formattedPaymentError(message));
    }
    yield put(ON_SPONSOR_GIFT.failure(isPaymentError));
  } finally {
    GARevenueTracking(
      gaReportedDonationAmount,
      submissionId,
      googleAnalyticsPaymentItems,
      attributionData
    );
    yield put(ON_SPONSOR_GIFT.fulfill());
  }
}

export default (api: ApisauceInstance) => {
  const onSponsorGiftRequestApi = (data: any) => api.post('/Sponsors', data);

  return [
    takeEvery(
      // @ts-ignore
      ON_SPONSOR_GIFT.TRIGGER,
      onSponsorGiftRequest,
      onSponsorGiftRequestApi
    ),
  ];
};
